/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The OLX card of the Portais settings, drawn with the same pieces as the
 * Facebook & Instagram card: an introduction with what the agency gets and
 * needs plus one brand-coloured connect button while disconnected, and a
 * network row (account, sending switch, connection health, disconnect) once
 * connected.
 */

import { useState } from 'react';
import { Anchor, Button, List, ListItem, StatusBadge, Text, Title, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatLocalizedDate } from '@/shared/utils/date';
import { DisconnectFooter, NetworkRow, Toggle } from '../FacebookSettingsSection/FacebookSettingsParts';
import { olxAcceptancePending } from './constants';
import type { PortalSettings } from './constants';

/** OLX brand purple. */
export const OLX_PURPLE = '#6E0AD6';
export const OLX_PLANS_URL = 'https://adquirir.olx.com.br/planos-imoveis';
/** OLX's "Meus anúncios" page, with the plan and its "Consumo do plano". */
export const OLX_MY_ADS_URL = 'https://conta.olx.com.br/anuncios/publicados';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface OlxConnectionBlockProps {
  settings: PortalSettings;
  /**
   * Disconnected as one row with a connect button (another portal is already
   * on, as Redes Sociais shows a network not connected yet) instead of the
   * full introduction.
   */
  compact?: boolean;
  enabled: boolean;
  autoPublish: boolean;
  saving: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  /** Opens the "is this your agency's account?" pop-up again. */
  onReviewAccount: () => void;
  /** Asks OLX again whether the account has a professional plan. */
  onCheckPlan: () => void;
  onToggleEnabled: () => void;
  onToggleAutoPublish: () => void;
  t: Translate;
}

/**
 * The OLX wordmark in a circle, as the Facebook/Instagram rows show theirs.
 *
 * @param props - Size in pixels and whether to draw it on the brand colour.
 * @returns The badge.
 */
export function OlxBadge({ size = 40, onBrand = true }: { size?: number; onBrand?: boolean }) {
  return (
    <Text
      component="span"
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-full font-extrabold tracking-tight shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.32),
        background: onBrand ? OLX_PURPLE : 'var(--surface-primary)',
        color: onBrand ? '#fff' : OLX_PURPLE,
      }}
    >
      OLX
    </Text>
  );
}

/**
 * OLX card: introduction when disconnected, network row when connected.
 *
 * @param props - Settings, switch states and actions.
 * @returns The card body.
 */
export function OlxConnectionBlock(props: OlxConnectionBlockProps) {
  if (!props.settings.olx_connected) {
    return props.compact
      ? <OlxConnectRow onConnect={props.onConnect} t={props.t} />
      : <OlxDisconnected onConnect={props.onConnect} t={props.t} />;
  }
  return <OlxConnected {...props} />;
}

/** One check-marked benefit line (same mark as the Facebook card). */
const CHECK = (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * "What you get" and "what you need" side by side, as on the Facebook card.
 *
 * @param props - Titles and lines of both lists.
 * @returns The two panels.
 */
export function IntroLists({ benefitsTitle, benefits, requirementsTitle, requirements }: {
  benefitsTitle: string; benefits: string[]; requirementsTitle: string; requirements: string[];
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-(--space-4) mt-(--space-6) mb-(--space-6)">
      <div className="rounded-(--radius-lg) bg-(--surface-secondary) p-(--space-4)">
        <Text className="m-0 text-xs font-semibold text-(--text-tertiary) uppercase tracking-wide mb-(--space-3)">{benefitsTitle}</Text>
        <List size="sm" spacing="sm" className="m-0 pl-0 list-none flex flex-col gap-(--space-2-5)">
          {benefits.map((txt) => (
            <ListItem key={txt} className="items-center text-sm text-(--text-primary)" icon={CHECK}>{txt}</ListItem>
          ))}
        </List>
      </div>
      <div className="rounded-(--radius-lg) bg-[var(--status-warning-bg)]/60 border border-[var(--status-warning)]/50 p-(--space-4)">
        <Text className="m-0 text-xs font-semibold text-[var(--status-warning-text)] uppercase tracking-wide mb-(--space-3)">{requirementsTitle}</Text>
        <List size="sm" spacing="sm" className="m-0 pl-0 list-none flex flex-col gap-(--space-2-5)">
          {requirements.map((txt) => (
            <ListItem
              key={txt}
              className="text-sm text-[var(--status-warning-text)]"
              icon={<Text component="span" className="block w-1.5 h-1.5 rounded-full bg-[var(--status-warning)]" />}
            >
              {txt}
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  );
}

/** The OLX network row offering the connect action. */
function OlxConnectRow({ onConnect, t }: { onConnect: () => void; t: Translate }) {
  return (
    <NetworkRow
      testId="network-row-olx"
      icon={<OlxBadge size={40} />}
      title="OLX"
      hint={t('olx.dicaConectar')}
      trailing={(
        <Button variant="outlined" size="xs" data-testid="portal-connect-olx" onClick={onConnect}>
          {t('olx.conectarCurto')}
        </Button>
      )}
    />
  );
}

/** The OLX panel shown before the agency connects its account. */
function OlxDisconnected({ onConnect, t }: { onConnect: () => void; t: Translate }) {
  return (
    <div data-testid="olx-disconnected">
      <div className="flex justify-center mb-(--space-4)">
        <div className="w-12 h-12 rounded-full bg-[var(--surface-primary)] border-2 border-(--surface-primary) flex items-center justify-center shadow-sm">
          <OlxBadge size={36} />
        </div>
      </div>

      <Title order={2} size="lg" className="m-0 text-center font-semibold text-(--text-primary)">{t('olx.titulo')}</Title>
      <Text className="m-0 mt-(--space-1) text-center text-sm text-(--text-secondary) leading-relaxed">{t('olx.descricao')}</Text>

      <IntroLists
        benefitsTitle={t('olx.beneficiosTitulo')}
        benefits={[t('olx.beneficio1'), t('olx.beneficio2'), t('olx.beneficio3')]}
        requirementsTitle={t('olx.prerequisitosTitulo')}
        requirements={[t('olx.prerequisito1'), t('olx.prerequisito2')]}
      />

      <UnstyledButton
        onClick={onConnect}
        data-testid="portal-connect-olx"
        className={cn(
          'w-full py-(--space-3) text-sm font-semibold rounded-(--radius-md) text-white',
          'flex items-center justify-center gap-(--space-2)',
          'cursor-pointer border-none transition-opacity hover:opacity-90',
        )}
        style={{ background: OLX_PURPLE }}
      >
        <OlxBadge size={18} onBrand={false} />
        {t('olx.conectar')}
      </UnstyledButton>

      <Text className="m-0 text-center text-xs text-(--text-tertiary) mt-(--space-3)">{t('olx.tempoModeracao')}</Text>
      <Text className="m-0 text-center text-xs text-(--text-secondary) mt-(--space-4)">
        {t('olx.ajudaPlano')}{' '}
        <Anchor href={OLX_PLANS_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-[var(--seed-blue)] hover:underline">
          {t('olx.ajudaPlanoLink')}
        </Anchor>
      </Text>
    </div>
  );
}

/** The OLX panel for a connected account: toggles, reconnect, plan check and disconnect. */
function OlxConnected({
  settings, enabled, autoPublish, saving, onConnect, onDisconnect, onCheckPlan, onReviewAccount,
  onToggleEnabled, onToggleAutoPublish, t,
}: OlxConnectionBlockProps) {
  const [confirming, setConfirming] = useState(false);
  const needsReconnect = settings.olx_token_status === 'invalid';
  // Pending: the pop-up shown after OLX's sign-in was closed without an answer,
  // or a connection that still has to accept the current OLX terms.
  const pending = !needsReconnect && olxAcceptancePending(settings);
  const termsOnly = pending && settings.olx_account_confirmed !== false;
  const planMissing = !needsReconnect && !pending && settings.olx_plan_status === 'missing';
  // Out of ads this cycle: new ads wait for the renewal, edits still go through.
  const quotaExhausted = !needsReconnect && !pending && !planMissing && settings.olx_block_reason === 'olx_no_slots';
  const account = settings.olx_user_name || settings.olx_user_email;

  return (
    <div className="flex flex-col gap-(--space-4)" data-testid="olx-connected">
      {needsReconnect && (
        <div
          className="px-(--space-4) py-(--space-3) rounded-(--radius-md) bg-[var(--status-warning-bg)] border border-[var(--status-warning-border)] flex items-center justify-between gap-(--space-3)"
          data-testid="olx-reconnect-notice"
        >
          <Text component="span" className="text-sm text-[var(--status-warning-text)]">{t('olx.reconectarAviso')}</Text>
          <UnstyledButton
            onClick={onConnect}
            data-testid="olx-reconnect-btn"
            className="shrink-0 text-xs font-semibold px-(--space-3) py-(--space-1-5) rounded-(--radius-sm) text-white border-none cursor-pointer"
            style={{ background: OLX_PURPLE }}
          >
            {t('olx.reconectar')}
          </UnstyledButton>
        </div>
      )}

      {planMissing && <OlxPlanNotice onCheckPlan={onCheckPlan} saving={saving} t={t} />}
      {quotaExhausted && (
        <OlxQuotaNotice renewsAt={settings.olx_plan_renews_at} onCheckPlan={onCheckPlan} saving={saving} t={t} />
      )}

      <NetworkRow
        testId="network-row-olx"
        icon={<OlxBadge size={40} />}
        title="OLX"
        identity={account ? t('olx.contaConectada', { account }) : t('olx.contaConectadaSemNome')}
        trailing={
          <Toggle
            checked={enabled && !planMissing && !pending}
            testId="toggle-olx"
            disabled={saving || needsReconnect || planMissing || pending}
            onClick={onToggleEnabled}
          />
        }
        meta={(
          <OlxStatusBadges
            settings={settings}
            needsReconnect={needsReconnect}
            planMissing={planMissing}
            pending={pending}
            termsOnly={termsOnly}
            t={t}
          />
        )}
        footer={
          <DisconnectFooter
            confirming={confirming}
            onTrigger={() => setConfirming(true)}
            onCancel={() => setConfirming(false)}
            onConfirm={() => { setConfirming(false); onDisconnect(); }}
            t={t}
            testIdPrefix="disconnect-olx"
            confirmMessage={t('olx.desconectarConfirmacao')}
            leading={pending ? (
              <UnstyledButton
                data-testid="olx-review-account-btn"
                onClick={onReviewAccount}
                className="text-xs font-semibold underline cursor-pointer bg-transparent border-none p-0"
                style={{ color: OLX_PURPLE }}
              >
                {termsOnly ? t('olx.aceitarTermos') : t('olx.confirmarConta')}
              </UnstyledButton>
            ) : undefined}
          />
        }
      />

      {enabled && !planMissing && !pending && settings.auto_publish_active && (
        <div className="flex items-start gap-(--space-3) pl-(--space-8)">
          <Toggle checked={autoPublish} size={16} testId="toggle-olx-autopublish" disabled={saving} onClick={onToggleAutoPublish} />
          <div className="flex-1">
            <Text component="span" className="text-sm font-medium text-(--text-primary)">{t('portal.publicacaoAutomatica')}</Text>
            <div className="text-sm text-(--text-secondary) mt-(--space-1) leading-relaxed">
              {autoPublish ? t('v2.portals.autoPublishOn') : t('v2.portals.autoPublishOff')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Connection health, plus a link to the plan on OLX itself. The balance API
 * counts plan insertions, not the ads live on the account, so a number here
 * read as wrong; OLX's own page shows both.
 */
function OlxStatusBadges({ settings, needsReconnect, planMissing, pending, termsOnly, t }: {
  settings: PortalSettings; needsReconnect: boolean; planMissing: boolean; pending: boolean; termsOnly: boolean;
  t: Translate;
}) {
  return (
    <>
      <StatusBadge tone={needsReconnect ? 'warning' : 'success'}>
        {needsReconnect ? t('olx.statusReconectar') : t('olx.statusAtivo')}
      </StatusBadge>
      {pending && (
        <StatusBadge tone="warning">{termsOnly ? t('olx.statusTermosPendentes') : t('olx.statusPendente')}</StatusBadge>
      )}
      {planMissing && <StatusBadge tone="warning">{t('olx.semPlano')}</StatusBadge>}
      {settings.olx_plan_status === 'active' && (
        <Anchor
          href={OLX_MY_ADS_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="olx-plan-link"
          className="text-xs font-semibold underline"
          style={{ color: OLX_PURPLE }}
        >
          {t('olx.verPlano')}
        </Anchor>
      )}
    </>
  );
}

/**
 * OLX said the account has no professional plan: publishing stays off, with
 * where to buy one and a way to re-check without reconnecting.
 */
/**
 * The plan's ads for this cycle are used up: new ads are held until the
 * plan renews (or a bigger plan is bought); editing published ads still works.
 */
function OlxQuotaNotice({ renewsAt, onCheckPlan, saving, t }: {
  renewsAt?: string | null; onCheckPlan: () => void; saving: boolean; t: Translate;
}) {
  const date = formatLocalizedDate(renewsAt);
  return (
    <div
      className="px-(--space-4) py-(--space-3) rounded-(--radius-md) bg-[var(--status-warning-bg)] border border-[var(--status-warning-border)] flex flex-col gap-(--space-3)"
      data-testid="olx-quota-notice"
    >
      <div>
        <Text className="m-0 text-sm font-semibold text-[var(--status-warning-text)]">{t('olx.limiteTitulo')}</Text>
        <Text className="m-0 mt-(--space-1) text-sm text-[var(--status-warning-text)] leading-relaxed">
          {date ? t('olx.limiteDescricao', { date }) : t('olx.limiteDescricaoSemData')}
        </Text>
      </div>
      <div className="flex flex-col sm:flex-row gap-(--space-2-5)">
        <Anchor
          href={OLX_PLANS_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="olx-upgrade-link"
          className="flex-1 text-center py-(--space-2-5) text-sm font-semibold rounded-(--radius-md) no-underline hover:opacity-90"
          style={{ background: OLX_PURPLE, color: 'var(--text-inverse)' }}
        >
          {t('olx.aumentarPlano')}
        </Anchor>
        <UnstyledButton
          onClick={onCheckPlan}
          disabled={saving}
          data-testid="olx-check-quota-btn"
          className="flex-1 py-(--space-2-5) text-sm font-semibold rounded-(--radius-md) border border-(--border-subtle) bg-[var(--surface-primary)] text-(--text-primary) cursor-pointer disabled:opacity-60"
        >
          {t('olx.verificarSaldo')}
        </UnstyledButton>
      </div>
    </div>
  );
}

/** Warning that the OLX account has no plan, with links to buy one and to re-check. */
function OlxPlanNotice({ onCheckPlan, saving, t }: { onCheckPlan: () => void; saving: boolean; t: Translate }) {
  return (
    <div
      className="px-(--space-4) py-(--space-3) rounded-(--radius-md) bg-[var(--status-warning-bg)] border border-[var(--status-warning-border)] flex flex-col gap-(--space-3)"
      data-testid="olx-plan-notice"
    >
      <div>
        <Text className="m-0 text-sm font-semibold text-[var(--status-warning-text)]">{t('olx.semPlanoTitulo')}</Text>
        <Text className="m-0 mt-(--space-1) text-sm text-[var(--status-warning-text)] leading-relaxed">{t('olx.semPlanoDescricao')}</Text>
      </div>
      <div className="flex flex-col sm:flex-row gap-(--space-2-5)">
        <Anchor
          href={OLX_PLANS_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="olx-plans-link"
          underline="never"
          className="flex-1 text-center py-(--space-2-5) text-sm font-semibold rounded-(--radius-md) text-white hover:text-white hover:opacity-90"
          style={{ background: OLX_PURPLE }}
        >
          {t('olx.verPlanos')}
        </Anchor>
        <UnstyledButton
          onClick={onCheckPlan}
          disabled={saving}
          data-testid="olx-check-plan-btn"
          className="flex-1 py-(--space-2-5) text-sm font-semibold rounded-(--radius-md) border border-(--border-subtle) bg-[var(--surface-primary)] text-(--text-primary) cursor-pointer disabled:opacity-60"
        >
          {t('olx.verificarNovamente')}
        </UnstyledButton>
      </div>
    </div>
  );
}
